import { useCallback } from 'react';
import { parseEntityRef } from '@mainstage/catalog-model';
import { CustomMicrofrontendOptions } from '../components/CustomMicrofrontendPage/CustomMicrofrontendConfiguration';
import { customAlertApiRef } from '../apis';
import { useApi } from '@mainstage/core-plugin-api';

/**
 * Hook for checking the visibility of custom microfrontends based on conditions
 *
 * @param instance - The current instance (data product, component, etc.)
 * @returns Function to check if a custom page should be shown
 */
export const useMicrofrontendVisibility = (
  useCaseTemplateId?: string,
  type?: string,
) => {
  const alertApi = useApi(customAlertApiRef);
  const showPage = useCallback(
    (pageConfig: CustomMicrofrontendOptions) => {
      if (!pageConfig.conditions) return true;

      const hasUseCase = Array.isArray(
        pageConfig.conditions.useCaseTemplateIds,
      );
      const hasTypes = Array.isArray(pageConfig.conditions.types);

      let useCaseTemplateMatch = true;
      if (hasUseCase) {
        useCaseTemplateMatch =
          !!useCaseTemplateId &&
          pageConfig.conditions.useCaseTemplateIds!.includes(useCaseTemplateId);
      }

      let typeMatch = true;
      if (hasTypes) {
        const parsedTypes = (pageConfig.conditions.types as unknown[])
          .filter((t): t is string => typeof t === 'string')
          .map((t: string) => {
            try {
              return parseEntityRef(t).name;
            } catch {
              alertApi.post({
                severity: 'error',
                message: `Invalid entity reference: ${t} for types condition in frontend plugin configuration. Frontend plugin will be disabled, please contact your Platform Team.`,
              });
              return undefined;
            }
          })
          .filter((name): name is string => Boolean(name));
        typeMatch = !!type && parsedTypes?.includes(type);
      }

      if (hasUseCase && hasTypes) {
        return useCaseTemplateMatch && typeMatch;
      }
      if (hasUseCase) {
        return useCaseTemplateMatch;
      }
      if (hasTypes) {
        return typeMatch;
      }
      return true;
    },
    [useCaseTemplateId, type, alertApi],
  );

  return { showPage };
};
